import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { File, InventoryExport, ProductMovePack } from 'depoto-core/src/entities'
import { OptionsConfig } from '../../configs'
import { HistoryUtil, SchemaUtil } from '../../utils'
import { CoreService, NotificationService } from '../../services'
import { DateUtil } from 'depoto-core/src/utils/DateUtil'
import { TranslateService } from '@ngx-translate/core'
import * as Sentry from '@sentry/angular-ivy'
import { FileUtilService } from '../../utils/file-util.service'
import * as XLSX from 'xlsx'

const INVENTORY_FILE_VALID_NUMBER_OF_COLUMNS = 16

@Component({
  selector: 'app-print-inventory-detail',
  templateUrl: 'print-inventory-detail.component.html',
  styleUrls: ['print-inventory-detail.component.scss'],
})
export class PrintInventoryDetailComponent implements OnInit, OnDestroy {
  inventoryExport: InventoryExport = new InventoryExport()
  isCreating = false
  loading = false
  error = false
  refreshInterval: any = null
  hasRoleInventoryApprove: boolean
  private firstLoad = true
  wizardActiveStep: 1 | 2 | 3 | 4 | 5 = 1
  isRightBlockShown: boolean = false
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private readonly fileUtil: FileUtilService,
  ) {}

  ngOnInit() {
    this.hasRoleInventoryApprove =
      this.core.services.user.hasRole('ROLE_INVENTORY_APPROVE') || this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    this.wizardActiveStep = 2
    this.route.params.forEach((params: Params) => {
      const id = +params['id']
      if (isNaN(id)) {
        this.isCreating = true
        this.inventoryExport = new InventoryExport()
        this.refreshData(null)
      } else {
        // note: load() is redundant here - refreshData does the same thing.. so, get rid off duplicate request on approved/rejected exports
        // this.load(id)
        // this.startRefreshing()
        this.refreshData(id)
      }
    })
  }

  // async load(id: number) {
  //   this.inventoryExport = await this.core.services.inventoryExport.getById(id, SchemaUtil.inventoryExport)
  //   if (!this.inventoryExport?.approved && !this.inventoryExport?.rejected) {
  //     this.regenerateExportXlsx()
  //   } else {
  //     this.refreshData(this.inventoryExport.id)
  //   }
  // }

  ngOnDestroy() {
    this.stopRefreshing()
  }

  async refreshData(id?: number) {
    if (!id) {
      return
    }
    this.loading = true
    // const shouldBeInventoryGenerated = this.inventoryExport.approved || this.inventoryExport.rejected
    const shouldBeInventoryGenerated = true // always
    if (shouldBeInventoryGenerated) {
      this.inventoryExport = {
        ...this.inventoryExport,
        ...(await this.core.services.inventoryExport.getById(
          this.inventoryExport?.id || id,
          SchemaUtil.inventoryExport,
        )),
      }
    }
    const isNotApprovedOrRejected = !this.inventoryExport.approved && !this.inventoryExport.rejected
    const hasGeneratedExportFile = !!this.inventoryExport.generated
    const hasUploadedFiles = this.inventoryExport.files?.length > 0
    const hasGeneratedPmpFile = this.inventoryExport.pmpIn?.id > 0 || this.inventoryExport.pmpOut?.id > 0
    if (!hasUploadedFiles && !hasGeneratedExportFile) {
      this.startRefreshing()
    }
    if (hasGeneratedExportFile && !hasUploadedFiles && isNotApprovedOrRejected) {
      this.stopRefreshing()
    }
    if (hasGeneratedExportFile && hasUploadedFiles && isNotApprovedOrRejected) {
      this.stopRefreshing()
      this.wizardActiveStep = 3
    }
    if (!isNotApprovedOrRejected && !hasGeneratedPmpFile) {
      this.wizardActiveStep = 4
      this.startRefreshing()
    }
    if (!isNotApprovedOrRejected && hasGeneratedPmpFile) {
      this.wizardActiveStep = 4
      // this.wizardActiveStep = 5
      this.stopRefreshing()
    }
    if (this.inventoryExport.generated && this.inventoryExport.url?.length && this.inventoryExport.files?.length) {
      if (
        isNotApprovedOrRejected ||
        (!isNotApprovedOrRejected && (this.inventoryExport.pmpIn?.url || this.inventoryExport.pmpOut?.url))
      ) {
        this.isRightBlockShown = true
      } else {
        this.isRightBlockShown = false
      }
    } else {
      this.isRightBlockShown = false
    }
    this.loading = false
  }

  async regenerateExportXlsx() {
    this.inventoryExport = {
      ...this.inventoryExport,
      ...(await this.core.services.inventoryExport.refreshExportInventory(
        this.inventoryExport,
        SchemaUtil.inventoryExport,
      )),
    }
    this.startRefreshing()
  }

  async handleFileUpload(event): Promise<void> {
    if (event.target.files[0] && this.inventoryExport.id > 0) {
      const isValid = await this.checkFileIntegrity(event.target.files[0])
      if (!isValid) {
        this.notificationService.error(this.translateService.instant('wizard.invalid-dialog.title'))
        return
      }
      this.loading = true
      this.inventoryExport.generated = null
      const reader = new FileReader()
      reader.readAsDataURL(new Blob([event.target.files[0]]))
      reader.onload = async evt => {
        const filename = event.target.value.split(/(\\|\/)/g).pop()
        const file: File = {
          id: null,
          text: filename,
          originalFilename: filename,
          mimeType: event.target.files[0].type,
          base64Data: String(evt.target['result']).replace('data:;base64,', ''),
          main: null,
          url: null,
          size: null,
          product: null,
          order: null,
          thumbnails: [],
        }
        try {
          await this.core.services.file.create(file, null, null, null, this.inventoryExport.id)
          await this.refreshData(this.inventoryExport.id)
          this.notificationService.success(this.translateService.instant('alert.file.uploaded'))
        } catch (err) {
          console.log(err)
        } finally {
          this.loading = false
        }
      }
      const processingError = this.translateService.instant('print-inventory.processing-error')
      reader.onerror = function () {
        alert(processingError)
      }
    }
  }

  async checkFileIntegrity(file: any): Promise<boolean> {
    return new Promise(resolve => {
      let isValid = false
      if (file) {
        if (file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
          return resolve(isValid)
        }
        const fileReader = new FileReader()
        fileReader.readAsArrayBuffer(file)
        fileReader.onload = e => {
          const arrayBuffer = fileReader.result
          const data = new Uint8Array(arrayBuffer as ArrayBuffer)
          const arr = []
          for (let i = 0; i != data.length; ++i) arr[i] = String.fromCharCode(data[i])
          const bstr = arr.join('')
          const workbook = XLSX.read(bstr, { type: 'binary' })
          const first_sheet_name = workbook.SheetNames[0]
          const header: Array<string> = XLSX.utils
            .sheet_to_json(workbook.Sheets[first_sheet_name], { header: 1 })
            .shift() as Array<string>
          const xlsxData = XLSX.utils.sheet_to_json(workbook.Sheets[first_sheet_name], { raw: true }).map(row => {
            const temp = {}
            header.forEach((key, index) => (temp[index] = row[key]))
            return temp
          })
          const itemCount = xlsxData.length
          const colsCount = itemCount > 0 && Object.keys(xlsxData[0]).length
          if (colsCount === INVENTORY_FILE_VALID_NUMBER_OF_COLUMNS) {
            isValid = true
          }
          resolve(isValid)
        }
      } else {
        resolve(isValid)
      }
    })
  }

  async approveInventory() {
    this.loading = true
    try {
      this.inventoryExport.approved = DateUtil.getDateStringFromUTCString(new Date().toUTCString())
      await this.core.services.inventoryExport.update(this.inventoryExport)
      this.inventoryExport = new InventoryExport(
        await this.core.services.inventoryExport.getById(this.inventoryExport.id, SchemaUtil.inventoryExport),
      )
      this.notificationService.success(this.translateService.instant('alert.inventory.approved'))
      this.startRefreshing()
    } catch (err) {
      console.log(err)
    } finally {
      this.loading = false
    }
  }

  async rejectInventory() {
    this.loading = true
    this.inventoryExport.rejected = DateUtil.getDateStringFromUTCString(new Date().toUTCString())
    await this.core.services.inventoryExport.update(this.inventoryExport)
    // this.inventoryExport = new InventoryExport(
    //   await this.core.services.inventoryExport.getById(this.inventoryExport.id, SchemaUtil.inventoryExport),
    // )
    await this.refreshData(this.inventoryExport.id)
    this.loading = false
  }

  navigateBack(): void {
    HistoryUtil.navigateBack()
  }

  startRefreshing(): void {
    if (!this.refreshInterval) {
      this.refreshInterval = setInterval(() => {
        this.refreshData(this.inventoryExport.id)
      }, OptionsConfig.stringsInventory.refreshIntervalTimeMS)
    }
  }

  stopRefreshing(): void {
    if (!this.refreshInterval && this.refreshBtnIsShown() && this.firstLoad) {
      this.firstLoad = false
      this.regenerateExportXlsx()
    }
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = undefined
    }
  }

  downloadGenerated(): void {
    this.fileUtil.downloadFile(this.inventoryExport.url, 'inventory-export.xlsx')
  }

  downloadGeneratedFinal(): void {
    if (
      this.inventoryExport &&
      this.inventoryExport.generated &&
      this.inventoryExport.generated.length > 0 &&
      this.inventoryExport.files &&
      this.inventoryExport.files.length > 0
    ) {
      this.fileUtil.downloadFile(
        this.inventoryExport.files[this.inventoryExport.files.length - 1].url,
        'inventory-export-final.xlsx',
      )
    }
  }

  downloadProductMovePack(pmp: ProductMovePack, type: 'in' | 'out'): void {
    if (pmp && pmp.url && pmp.url.length > 0) {
      this.fileUtil.downloadFile(pmp.url, `pmp-${type}.pdf`)
    }
  }

  isGeneratedFinal(): boolean {
    return (
      this.inventoryExport.files &&
      this.inventoryExport.files.length > 0 &&
      this.inventoryExport.generated &&
      this.inventoryExport.generated.length > 0
    )
  }

  refreshBtnIsShown(): boolean {
    return (
      this.inventoryExport &&
      this.inventoryExport.generated &&
      !this.inventoryExport.approved &&
      !this.inventoryExport.rejected &&
      !this.isGeneratedFinal()
    )
  }

  isGeneratedPmps(): boolean {
    return !(!!this.inventoryExport.approved && !this.inventoryExport.finished)
  }

  triggerBackBtn() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.router.navigate(['/inventory-exports'])
    }
  }
}
